import { useCustomizationStore } from '@/features/theme-editor/stores/customizationStore';
import { useThemeEditorStore } from '@/features/theme-editor/stores/themeEditorStore';
import { getColorCollection } from '@/features/theme-editor/utils/colors';
import { Colors as TColors } from '@/types/store';
import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { Colors } from '../../../constants/themeConstants';
import { MenuItem, useMenuNavigation } from '../../Menu';
import { Panel, PanelFooter } from '../../Panel';
import { RevertButton } from '../../Panel/PanelFooter';
import { ColorIcon } from '../../ui/ColorIcon';
import { GroupLabel } from '../../ui/GroupLabel';
import { Icon } from '../../ui/Icon';
import { EditColors } from './EditColors/EditColors';

// Helper component for rendering color items with custom settings button
const ColorsItemRenderer = () => {
    const { push } = useMenuNavigation();

    return (
        <button
            className='cursor-pointer text-[24px]'
            onClick={e => {
                e.stopPropagation(); // Prevent triggering the parent route's onClick
                push?.('Edit colors', <EditColors />);
            }}
        >
            <Icon name='settings' />
        </button>
    );
};

export const ColorsView = () => {
    const { colors, updateStoreCustomizations, cachedState } = useCustomizationStore();
    const { customColors, setCustomColors } = useThemeEditorStore();
    const colorCollection = getColorCollection(colors ?? undefined);

    // On mount, evaluate if the custom colors are different from the store colors
    useEffect(() => {
        if (!colors) return;
        const collection = getColorCollection(colors);
        if (!collection) setCustomColors({ ...colors });
    }, []);

    const [cachedCustomColors] = useState<TColors | null>(cachedState?.colors ?? null);
    const isDirty = !isEqual(cachedCustomColors, customColors ?? colors);

    const resetBlock = () => {
        if (cachedCustomColors) updateStoreCustomizations({ colors: cachedCustomColors });
    };

    return (
        <Panel footer={<PanelFooter>{isDirty && <RevertButton onClick={resetBlock} />}</PanelFooter>}>
            <div className='w-full space-y-2'>
                {customColors && (
                    <>
                        <GroupLabel group='Custom preset' label='(Your custom swatches)' />
                        <MenuItem
                            label='Custom preset'
                            description='This is your unique color set.'
                            icon={
                                <ColorIcon primary={customColors.bg.primary} secondary={customColors.accent.primary} />
                            }
                            active={!colorCollection}
                            onClick={() => updateStoreCustomizations({ colors: customColors })}
                            className='items-center'
                        >
                            <ColorsItemRenderer />
                        </MenuItem>
                    </>
                )}

                <GroupLabel group='Color presets' label='(start from a curated palette)' />
                {Colors.map(color => {
                    const active = colorCollection === color.id;
                    return (
                        <MenuItem
                            key={color.id}
                            label={color.name}
                            description={color.description}
                            icon={
                                <ColorIcon primary={color.colors.bg.primary} secondary={color.colors.accent.primary} />
                            }
                            active={active}
                            onClick={() => updateStoreCustomizations({ colors: color.colors })}
                            className='items-center'
                        >
                            <ColorsItemRenderer />
                        </MenuItem>
                    );
                })}
            </div>
        </Panel>
    );
};
